import React, { useState, useEffect } from 'react';
import { MessageSquare, Lock, Bug, Target, BarChart2, CheckCircle, BookOpen, Radio } from 'lucide-react';

export default function GlobalTicker({ ctx }) {
  const { T, maintenanceMode, api, isEN } = ctx;
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api('/ticker');
        setItems(data);
      } catch(e) {}
    };
    
    load();
    const int = setInterval(load, 15000); // Ticker lädt alle 15 Sekunden frische Daten
    
    window.addEventListener('new_broadcast', load);
    window.addEventListener('wiki_updated', load);
    
    return () => {
      clearInterval(int);
      window.removeEventListener('new_broadcast', load);
      window.removeEventListener('wiki_updated', load);
    };
  }, [api]);

  useEffect(() => {
    if (items.length <= 1) return;
    const int = setInterval(() => {
      setCurrentIndex(oldIndex => (oldIndex + 1) % items.length);
    }, 8000); // Wechselt alle 8 Sekunden zur nächsten Meldung
    return () => clearInterval(int);
  }, [items.length]);

  // 🚨 WARTUNGSMODUS-PILLE
  if (maintenanceMode) {
    return (
      <div style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)", color: "#F59E0B", padding: "6px 20px", borderRadius: 100, display: "flex", justifyContent: "center", alignItems: "center", gap: 8, maxWidth: 500, width: "100%", boxShadow: "0 4px 12px rgba(245,158,11,0.1)" }}>
        <span style={{ animation: "pulseWarning 2s infinite" }}>⚠️</span>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.05em" }}>
          {isEN ? "MAINTENANCE MODE ACTIVE" : "WARTUNGSMODUS"}
        </span>
        <style>{`@keyframes pulseWarning { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }`}</style>
      </div>
    );
  }

  // WENN KEINE TICKER-MELDUNGEN DA SIND
  if (!items || items.length === 0) {
    return (
      <div style={{ margin: "0 auto", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 100, height: 34, padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "center", maxWidth: 450, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle size={14} color="#10B981"/>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.textSub }}>{isEN ? "All systems operational." : "Alle Systeme laufen reibungslos."}</span>
        </div>
      </div>
    );
  }

  const currentItem = items[currentIndex] || items[0];

  // 🚀 BAUT DEN RICHTIGEN TEXT & DAS ICON ZUSAMMEN
  const renderItem = function(item) {
    if (!item) return { icon: <CheckCircle size={14} color="#10B981"/>, text: "" };

    if (item.type === 'admin') return { icon: <Radio size={14} color="#0ECFD9"/>, text: item.text };
    if (item.type === 'wiki') return { icon: <BookOpen size={14} color="#F472B6"/>, text: isEN ? `Wiki Update: ${item.title}` : `Wiki Update: ${item.title}` };
    if (item.type === 'beta') return { icon: <Bug size={14} color="#EF4444"/>, text: isEN ? `New Beta ${item.version} available!` : `Neue Beta ${item.version} verfügbar!` };
    if (item.type === 'poll') return { icon: <BarChart2 size={14} color="#8B5CF6"/>, text: isEN ? `Poll: ${item.title}` : `Umfrage: ${item.title}` };
    if (item.type === 'nl2') return { icon: <Lock size={14} color="#F59E0B"/>, text: isEN ? `NL2 locked by ${item.user}` : `NL2 Projekt wird bearbeitet von ${item.user}` };
    if (item.type === 'roadmap') return { icon: <Target size={14} color="#34D399"/>, text: isEN ? `Roadmap: ${item.pct}% (${item.done}/${item.total})` : `Roadmap Fortschritt: ${item.pct}% (${item.done}/${item.total})` };

    return { icon: <CheckCircle size={14} color="#10B981"/>, text: isEN ? "All systems operational." : "Alle Systeme laufen reibungslos." };
  };

  const display = renderItem(currentItem);

  return (
    <div style={{ margin: "0 auto", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 100, height: 34, padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", maxWidth: 450, width: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
      <div key={currentItem.id} style={{ display: "flex", alignItems: "center", gap: 8, animation: "tickerSlide 0.8s cubic-bezier(0.4, 0, 0.2, 1)" }}>
        <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>{display.icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.textSub, letterSpacing: "0.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{display.text}</span>
      </div>
      <style>{`
        @keyframes tickerSlide {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}